<template>
  <div class="text-editor-wrap">
    <span v-if="label" class="label-style">{{ label }}</span>
    <v-container fluid class="text-editor">
      <v-col v-if="editor !== null && !readonly">
        <v-row class="editor-tools">
          <editor-action-button
            v-for="(action, index) in actions"
            :key="index"
            :action="action"
            :is-active="editor.isActive(action.name)"
            :loading="loading"
          />
          <text-color :editor="editor" :loading="loading" />
          <text-size :editor="editor" :loading="loading" />
        </v-row>
      </v-col>
      <v-col class="mt-3" style="padding: 0">
        <editor-content class="editor-content" :editor="editor" />
      </v-col>
      <LinkDialog
        v-if="linkDialog"
        :initial-link="linkContent"
        :initial-target="linkTarget"
        @close="closeLinkDialog"
        @save="setLink"
      />
      <ImageDialog
        v-if="imageDialog"
        :initial-image="imageContent"
        @close="closeImageDialog"
        @save="setImage"
      />
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import { Color } from '@tiptap/extension-color'
import { TextStyle, FontSize } from '@tiptap/extension-text-style'
import { Placeholder } from '@tiptap/extensions'
import TextAlign from '@tiptap/extension-text-align'
import ImageResize from 'tiptap-extension-resize-image'

import TextSize from './extensions/TextSize.vue'
import TextColor from './extensions/TextColor.vue'
import EditorActionButton from './components/EditorActionButton.vue'
import { ref, watch, onBeforeUnmount } from 'vue'
import type Action from '@/types/Action'
import LinkDialog from './components/LinkDialog.vue'
import ImageDialog from './components/ImageDialog.vue'

interface Props {
  label?: string
  loading?: boolean
  modelValue?: string | null
  placeholder?: string
  readonly?: boolean
}

const {
  label = '',
  loading = false,
  modelValue = '',
  placeholder = '...',
  readonly = false,
} = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const linkDialog = ref(false)
const linkContent = ref('')
const linkTarget = ref('')
const imageDialog = ref(false)
const imageContent = ref('')

const editor: Editor = new Editor({
  editorProps: {
    attributes: {
      class: 'prose',
    },
  },
  content: modelValue ?? '',
  extensions: [
    StarterKit.configure({
      bulletList: {
        itemTypeName: 'listItem',
        keepMarks: true,
        keepAttributes: true,
        HTMLAttributes: { class: 'bullet-list-tiptap' },
      },
      link: {
        protocols: ['http', 'https', 'mailto'],
        openOnClick: false,
        enableClickSelection: false,
        validate: (href: string) => /^https?:\/\//.test(href),
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      },
    }),
    ImageResize,
    Placeholder.configure({ placeholder: placeholder }),
    Color.configure({ types: [ 'textStyle' ] }),
    TextStyle.configure({ mergeNestedSpanStyles: true }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    FontSize,
  ],
  onUpdate: () => {
    emit('update:modelValue', editor.getHTML())
  },
  editable: !readonly && !loading,
})

const openImageDialog = () => {
  const imageAttrs = editor.getAttributes('image')
  imageContent.value = (imageAttrs.src as string) ?? ''
  imageDialog.value = true
}

const closeImageDialog = () => {
  imageDialog.value = false
}

const openLinkDialog = () => {
  const linkAttrs = editor.getAttributes('link')
  linkContent.value = (linkAttrs.href as string) ?? ''
  linkTarget.value = (linkAttrs.target as string) ?? ''
  linkDialog.value = true
}

const closeLinkDialog = () => {
  if (linkContent.value === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
  }
  linkDialog.value = false
}

const setLink = (linkData: { href: string, target: string }) => {
  if (linkData.href === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
  } else {
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: linkData.href, target: linkData.target })
      .run()
  }
  linkDialog.value = false
}

const setImage = (imageData: { src: string }) => {
  if (imageData.src.trim() !== '') {
    editor
      .chain()
      .focus()
      .setImage({ src: imageData.src })
      .run()
  } else {
    editor.chain().focus().deleteSelection().run()
  }
  imageDialog.value = false
}

const actions = ref<Action[]>([
  {
    name: 'bold',
    icon: 'fa-regular fa-bold',
    group: false,
    callback: () => editor.chain().focus().toggleBold().run(),
  },
  {
    name: 'italic',
    icon: 'fa-regular fa-italic',
    group: false,
    callback: () => editor.chain().focus().toggleItalic().run(),
  },
  {
    name: 'underline',
    icon: 'fa-regular fa-underline',
    group: false,
    callback: () => editor.chain().focus().toggleUnderline().run(),
  },
  {
    name: 'heading',
    icon: 'fa-regular fa-h1',
    group: false,
    callback: () =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    name: 'align',
    icon: 'fa-regular fa-align-left',
    group: true,
    items: [
      {
        name: 'align-left',
        icon: 'fa-regular fa-align-left',
        callback: () => editor.chain().focus().setTextAlign('left').run(),
        active: true,
        group: false,
      },
      {
        name: 'align-center',
        icon: 'fa-regular fa-align-center',
        callback: () =>
          editor.chain().focus().setTextAlign('center').run(),
        active: false,
        group: false,
      },
      {
        name: 'align-right',
        icon: 'fa-regular fa-align-right',
        callback: () =>
          editor.chain().focus().setTextAlign('right').run(),
        active: false,
        group: false,
      },
      {
        name: 'align-justify',
        icon: 'fa-regular fa-align-justify',
        callback: () =>
          editor.chain().focus().setTextAlign('justify').run(),
        active: false,
        group: false,
      },
    ],
  },
  {
    name: 'bullet_list',
    group: false,
    icon: 'fa-regular fa-list-ul',
    callback: () => editor.chain().focus().toggleBulletList().run(),
  },
  {
    name: 'ordered_list',
    group: false,
    icon: 'fa-regular fa-list-ol',
    callback: () => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    name: 'image',
    group: false,
    icon: 'fa-regular fa-image',
    callback: () => {
      openImageDialog()
      editor.chain().focus()
    },
  },
  {
    name: 'link',
    group: false,
    icon: 'fa-regular fa-link',
    callback: () => {
      openLinkDialog()
      editor.chain().focus()
    },
  },
])

watch(() => modelValue, (value) => {
  const isSame = editor.getHTML() === value

  if (isSame) {
    return
  }

  editor.commands.setContent(value ?? '', { emitUpdate: false })
})

watch(() => readonly, (value) => {
  if (value) editor.setEditable(!value)
})

watch(() => loading, (value) => {
  editor.setEditable(!value)
})

onBeforeUnmount(() => {
  editor.destroy()
})
</script>

<style lang="scss">
.editor-action-active {
  color: black;
}

.label-style {
  position: relative;
  top: 10px;
  left: 16px;
  font-size: 12px;
  background: white;
  height: 18px;
  padding: 0px 4px;
}

.text-editor,
.ProseMirror,
.editor-tools {
  border: 1px solid #ccc;
  border-radius: 4px;
}

.ProseMirror {
  padding: 16px;
}

.ProseMirror-focused {
  border-color: #494949;
}

.tiptap {
  :first-child {
    margin-top: 0;
  }

  /* List styles */
  ul,
  ol {
    padding: 0 1rem;
    margin: 1.25rem 1rem 1.25rem 0.4rem;

    li p {
      margin: 0.25em 0;
    }
  }
}

.editor-tools {
  border-radius: 4px;
}
</style>
